import { createContext } from 'react';
import { decorate, observable, action, toJS, computed } from 'mobx';

import * as StorageKeys from '../constants/storageKeys';
import * as AccessType from '../constants/accessTypes';
import * as fn from '../utilities/_functions';
import * as ach from '../utilities/accessHelper';

import api from '../api';

export class PredefinedExamView {
    newTemplate = {
        name: null,
        isShared: false,
    };
    data = null;
    examTemplates = [];
    userPredefinedTemplates = [];
    sharedPredefinedTemplates = [];
    selectedExamTemplateId = null;
    selectedPredefinedTemplateId = null;
    isLoading = false;
    isSaving = false;
    isReady = false;
    hasUnsavedChanges = false;

    cancelExamTemplates = null;
    cancelPublicPredefinedData = null;
    cancelUserPredefinedData = null;
    cancelUserPredefinedDataUpdate = null;

    initialize = () => {
        const that = this;
        this.clear();

        return that.refresh()
            .finally(() => {
                that.isReady = true;
            })
    }

    refresh = () => {
        const that = this;

        return new Promise((resolve, reject) => {
            const currentUser = JSON.parse(window.localStorage.getItem(StorageKeys.CURRENT_USER));
            that.isLoading = true;

            api.ExamTemplates.search(
                {
                    parameters: [
                        {
                            field: 'PublishedTemplateId',
                            operator: '!=',
                            value: null
                        },
                        {
                            field: 'IsSystemTemplate',
                            value: false
                        },
                        {
                            field: 'IsDefault',
                            value: true
                        },
                        {
                            field: 'DeactivatedDateUtc',
                            value: null
                        }
                    ],
                    sortByFields: [{
                        field: 'CreatedDateUtc',
                        direction: 'DESC',
                    }],
                    includeTotalCount: false,
                    loadProperties: false,
                },
                (c) => { that.cancelExamTemplates = c }
            )
                .then(({ data }) => {
                    that.examTemplates = data && data.result && data.result.length > 0 ? data.result : [];

                    if (!that.selectedExamTemplateId) {
                        that.selectedExamTemplateId = that.examTemplates.length > 0 ? that.examTemplates[0].id : null;
                    }

                    const searchParameters = [
                        { field: 'DeactivatedDateUtc', value: null },
                        { field: 'ExamTemplateId', operator: 'Contains', value: that.examTemplates.map(t => { return t.id }).join(',') }
                    ];
                    const searchSortFields = [{ field: 'Name', direction: 'ASC' }];

                    Promise.all([
                        api.PredefinedData.search(
                            {
                                parameters: [
                                    ...searchParameters,
                                    ...[{ field: 'UserId', value: currentUser.id, }]
                                ],
                                sortByFields: searchSortFields,
                            },
                            (c) => { that.cancelUserPredefinedData = c }
                        ),
                        api.PredefinedData.search(
                            {
                                parameters: [
                                    ...searchParameters,
                                    ...[{ field: 'UserId', value: null, }]
                                ],
                                sortByFields: searchSortFields,
                            },
                            (c) => { that.cancelPublicPredefinedData = c }
                        ),
                    ])
                        .then(response => {
                            const userPredefinedDataSets = response[0].data.result ? response[0].data.result : [];
                            const publicPredefinedDataSets = response[1].data.result ? response[1].data.result : [];

                            that.userPredefinedTemplates = userPredefinedDataSets;
                            that.sharedPredefinedTemplates = publicPredefinedDataSets;

                            if (!that.selectedPredefinedTemplateId) {
                                if (that.userPredefinedTemplates.length > 0) {
                                    that.selectedPredefinedTemplateId = that.userPredefinedTemplates[0].id;
                                }
                                else if (that.sharedPredefinedTemplates.length > 0 && ach.check(AccessType.UPDATE_EXAM_PREDEFINED_DATA_SHARED)) {
                                    that.selectedPredefinedTemplateId = that.sharedPredefinedTemplates[0].id;
                                }    
                            }

                            resolve();
                        })
                        .catch((error) => {
                            reject(error);
                        })
                        .finally(() => {
                            that.isLoading = false;
                        })
                })
                .catch((error) => {
                    reject(error);
                })
        })
    }

    save = () => {
        const that = this;

        that.isSaving = true;

        return new Promise((resolve, reject) => {
            const option = { ...{ examTemplateId: that.selectedExamTemplateId }, ...toJS(that.newTemplate) };

            api.PredefinedData.create(option, (c) => { that.cancelUserPredefinedDataUpdate = c })
                .then(({ data }) => {
                    that.clearNewTemplate();
                    that.hasUnsavedChanges = false;
                    that.refresh()
                        .then(() => {
                            that.selectedPredefinedTemplateId = data && data.id ? data.id : null;
                            resolve(data);
                        })
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    that.isSaving = false;
                })
        })
    }

    delete = () => {
        const that = this;

        that.isSaving = true;

        return new Promise((resolve, reject) => {
            if (that.selectedPredefinedTemplateId) {
                api.PredefinedData.delete(that.selectedPredefinedTemplateId, (c) => { that.cancelUserPredefinedDataUpdate = c })
                    .then(() => {
                        that.selectedPredefinedTemplateId = null;
                        resolve();
                    })
                    .catch((error) => {
                        reject(error);
                    })
                    .finally(() => {
                        that.isSaving = false;
                    })
            }
        })
    }

    get selectedExamTemplate() {
        if (this.selectedExamTemplateId) {
            const result = this.examTemplates.find(t => t.id === this.selectedExamTemplateId);
            return result;
        }

        return null;
    }

    get selectedUserPredefinedTemplates() {
        if (this.selectedExamTemplateId && this.userPredefinedTemplates) {
            const result = this.userPredefinedTemplates.filter(t => t.examTemplateId === this.selectedExamTemplateId);
            return result && result.length > 0 ? result : [];
        }

        return [];
    }

    get selectedPublicPredefinedTemplates() {
        if (this.selectedExamTemplateId && this.sharedPredefinedTemplates) {
            const result = this.sharedPredefinedTemplates.filter(t => t.examTemplateId === this.selectedExamTemplateId);
            return result && result.length > 0 ? result : [];
        }

        return [];
    }

    get selectedPredefinedTemplate() {
        if (this.selectedPredefinedTemplateId) {
            const result = [...this.userPredefinedTemplates, ...this.sharedPredefinedTemplates].find(t => t.id === this.selectedPredefinedTemplateId);
            return result;
        }

        return null;
    }

    clear = () => {
        this.data = null;
        this.clearNewTemplate();
        this.examTemplates.clear();
        this.userPredefinedTemplates.clear();
        this.sharedPredefinedTemplates.clear();
        this.selectedTemplateId = null;
        this.selectedPredefinedTemplateId = null;
        this.isLoading = false;
        this.isSaving = false;
        this.isReady = false;
        this.hasUnsavedChanges = false;

        if (fn.isFunction(this.cancelExamTemplates)) {
            this.cancelExamTemplates();
            this.cancelExamTemplates = null;
        }

        if (fn.isFunction(this.cancelPublicPredefinedData)) {
            this.cancelPublicPredefinedData();
            this.cancelPublicPredefinedData = null;
        }

        if (fn.isFunction(this.cancelUserPredefinedData)) {
            this.cancelUserPredefinedData();
            this.cancelUserPredefinedData = null;
        }
    }

    clearNewTemplate = () => {
        this.newTemplate.name = null;
        this.newTemplate.isShared = false;
    }
}

decorate(PredefinedExamView, {
    data: observable,
    newTemplate: observable,
    examTemplates: observable,
    userPredefinedTemplates: observable,
    sharedPredefinedTemplates: observable,
    selectedExamTemplateId: observable,
    selectedPredefinedTemplateId: observable,
    isLoading: observable,
    isSaving: observable,
    isReady: observable,
    hasUnsavedChanges: observable,
    save: action,
    delete: action,
    clear: action,
    clearNewTemplate: action,
    selectedExamTemplate: computed,
    selectedUserPredefinedTemplates: computed,
    selectedPublicPredefinedTemplates: computed,
    selectedPredefinedTemplate: computed,
})

export default createContext(new PredefinedExamView());